/**
 * Types and pure helpers for the Colocation rack-U grid.
 *
 * The `metadata` field on a `colocation-u` / `u` configoption carries the
 * racks array. This shape is derived from the legacy Main/AddonServiceColocation
 * components and from the `commitment_lastest_metadata` field observed in staging
 * configoption responses.
 */

/** One rack assignment stored inside a configoption's metadata. */
export interface RackPosition {
  start_u: number;
  end_u: number;
}

export interface RackEntry {
  /** IDC location label, e.g. "VNPT Long Thanh". */
  idc: string;
  /** Rack identifier within the IDC, e.g. "R01-05". */
  rack: string;
  /** U-slot range (nullable when not yet assigned). */
  position: RackPosition | null;
}

/**
 * Shape of the `metadata` field on a colocation-u / u configoption.
 * The `racks` array is the new multi-rack format; the legacy single-rack
 * format (`rack.name`, `rack.idc`) is migration-read-only in the legacy UI
 * and is not reproduced here.
 */
export interface ColocationConfigoptionMetadata {
  racks?: RackEntry[];
  [key: string]: unknown;
}

/**
 * Structural subset of ServiceConfigOption plus the colocation metadata field.
 * No index signature so plain ServiceConfigOption rows (which don't declare
 * `metadata` but carry it in the raw JSON) remain assignable.
 */
export interface ColocationConfigOption {
  service_configoption_id: number;
  service_id: number;
  name: string;
  commitment_value?: number;
  metadata?: ColocationConfigoptionMetadata;
}

/** Extend the addon service row to carry configoptions and a service_name. */
export interface ColocationAddon {
  service_id: number;
  service_name: string;
  configoptions?: ColocationConfigOption[];
}

/** Name values used to detect the rack-U configoption (legacy enum parity). */
export const COLOCATION_U_NAMES = new Set(['colocation-u', 'u']);

/** Find the rack-U configoption in a list, or undefined if absent. */
export function findColocationUConfig(
  configoptions: ColocationConfigOption[],
): ColocationConfigOption | undefined {
  return configoptions.find((c) => COLOCATION_U_NAMES.has(c.name));
}

/** Total U occupied by a single rack entry. */
export function rackU(entry: RackEntry): number {
  if (!entry.position) return 0;
  return Math.max(0, entry.position.end_u - entry.position.start_u + 1);
}

/** Total U occupied across all rack entries. */
export function totalU(racks: RackEntry[]): number {
  return racks.reduce((sum, r) => sum + rackU(r), 0);
}

/** Format U position like legacy: "[1]" or "[1-4]". */
export function formatUPosition(pos: RackPosition | null): string {
  if (!pos) return '—';
  if (pos.start_u === pos.end_u) return `[${pos.start_u}]`;
  return `[${pos.start_u}–${pos.end_u}]`;
}
